import { BackgroundCheckError, getSsn } from '@/backend/features/background-check';
import { BackgroundCheckConfig, BackgroundCheckQuota } from '@/backend/features/background-check/defaults';
import {
    checkFirstName,
    checkLastName,
    commonBackgroundCheckRequestValidation,
} from '@/backend/features/background-check/platform-functions/utils/validateBackgroundCheckRequestParams';
import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckTypeJnet, jnetBackgroundCheckTypes } from '@/backend/features/background-check/types';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { AppContext } from '@juvare/platform-app/App';
import { AppState } from '@juvare/platform-sdk-react/types';

interface ValidateJnetRequestParams {
    context: AppContext<AppState>;
    accountData: ExtendedAccountData;
    backgroundCheck: BackgroundCheckColumns;
    config: BackgroundCheckConfig;
    quota: BackgroundCheckQuota;
}

export interface ValidateJnetRequestResponse {
    jasType: BackgroundCheckTypeJnet;
    person: {
        firstName: string;
        lastName: string;
        /**
         * ISO style date
         * @example 1931-10-13
         */
        dob: string;
        ssn: string;
    };
    isUsingQuota?: boolean;
}

export function validateJnetRequest({
    context,
    accountData,
    backgroundCheck,
    config,
    quota,
}: ValidateJnetRequestParams): ValidateJnetRequestResponse {
    if (!config.type.jnet.requiresFormValidation) {
        throw new Error('JNET request require form validation, please enable it in the backround check configuration');
    }

    const jasType = jnetBackgroundCheckTypes.jnet.jasType;

    commonBackgroundCheckRequestValidation({
        context,
        backgroundCheck,
        backgroundCheckType: 'jnet',
        config,
        quota,
    });
    checkFirstName(accountData);
    checkLastName(accountData);

    if (!accountData.dateOfBirth) {
        throw new BackgroundCheckError('Date of birth is required', 'missingFields');
    }

    const ssn = getSsn(context, accountData);
    if (!ssn) {
        throw new BackgroundCheckError('SSN is required', 'missingFields');
    }

    return {
        jasType,
        person: {
            firstName: accountData.firstName,
            lastName: accountData.lastName,
            dob: accountData.dateOfBirth,
            ssn,
        },
        isUsingQuota: quota.jnet.enabled,
    };
}
